import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { parseEnvironment } from '@ochal/config';
import { API_CLIENT, ApiClient } from '@ochal/api-client';
import { ApiAuthAdapter } from '@ochal/auth';
import { PluginRuntime } from '@ochal/plugin-runtime';
import { FeatureFlagRuntime } from '@ochal/plugin-runtime';
import '@ochal/ui/styles.css';
import './styles.css';
import './responsive-polish.css';
import './login-v3.css';
import './shell-v3.css';
import './premium-workspace.css';
import { getFeatureFlags } from './config/feature-flags';
import { fa } from './i18n/fa';
import { pluginCatalog } from './modules';
import { MaintenancePage } from './pages/system-pages';
import { AppProviders, StartupScreen } from './providers';
import { createAppRouter } from './router';
import { notifySessionExpired, SessionExpiryBridge } from './components/session-expiry-bridge';

const root = createRoot(document.getElementById('root')!);
async function bootstrap(): Promise<void> {
  try {
    const environment = parseEnvironment(import.meta.env);
    const runtime = new PluginRuntime({
      features: new FeatureFlagRuntime(getFeatureFlags(environment)),
    });
    const authAdapter = new ApiAuthAdapter(
      environment.VITE_API_BASE_URL,
      environment.VITE_API_TIMEOUT_MS,
    );
    runtime.services.provide(
      API_CLIENT,
      new ApiClient({
        baseUrl: environment.VITE_API_BASE_URL,
        timeout: environment.VITE_API_TIMEOUT_MS,
        getAccessToken: authAdapter.getAccessToken,
        refreshAccessToken: () => authAdapter.refreshAccessToken(),
        onUnauthorized: notifySessionExpired,
      }),
    );
    await runtime.start(pluginCatalog);
    const content = environment.VITE_MAINTENANCE_MODE ? (
      <MaintenancePage />
    ) : (
      <>
        <StartupScreen />
        <RouterProvider router={createAppRouter(runtime)} />
      </>
    );
    root.render(
      <StrictMode>
        <AppProviders runtime={runtime} authAdapter={authAdapter}>
          <SessionExpiryBridge />
          {content}
        </AppProviders>
      </StrictMode>,
    );
  } catch {
    root.render(
      <main className="error-page" role="alert">
        <h1>{fa.state.configurationTitle}</h1>
        <p>{fa.state.configurationDescription}</p>
      </main>,
    );
  }
}
void bootstrap();
